import React, { useState, useEffect } from "react";
import { Form, Input,  Button, Space, Table, Modal, Tooltip } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import axios from 'axios';
import { URLS } from "../constants/baseURLS";

const { TextArea } = Input;

interface Course {
  courseName: string;
  instructorName: string;
  duration: string;
  courseDescription: string;
  seats: string;
  price: string;
  language: string;
  lessons: string;
  enrolled: string;
  courseContent: string;
  // Backend uses `published`; keep legacy isPublished for fallback
  published?: boolean;
  isPublished?: boolean;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  userPostDataInfo?: Array<{
    name: string;
    role: string;
    emailId: string;
    createdAt: string;
    updatedAt: string;
  }>;
  _id?: string;
}


const CoursesForm: React.FC = () => {
  const [form] = Form.useForm();
  const [successData, setSuccessData] = useState<Course[]>([]);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [editCourseIndex, setEditCourseIndex] = useState<any | null>(null);
  // Filter state for publish/unpublish
  const [publishFilter, setPublishFilter] = useState<'all' | 'published' | 'unpublished'>('all');

  // Get user role from localStorage
  const getUserRole = (): string => {
    const userDetailsString = localStorage.getItem('userDetails');
    const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
    return userDetails?.role || '';
  };
  const userRole = getUserRole();

  const handleUpdate = async (courseId: string, updatedData: Partial<Course>) => {
    try {
      const response = await axios.patch(`${URLS.DEV}v1/edit-course/${courseId}`, updatedData);
      console.log("Course updated:", response.data);
      const updatedCourse = response.data;
      const updatedCourses = successData.map(course =>
        course._id === updatedCourse.key ? updatedCourse : course
      );
      setSuccessData(updatedCourses);
    } catch (error) {
      console.error("Error updating course:", error);
    }
  };

  const onFinish = async (values: Course) => {
    // Get userId from localStorage
    const userDetailsString = localStorage.getItem('userDetails');
    const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
    const userId = userDetails?.userId;

    if (editCourseIndex !== null) {
      try {
        await handleUpdate(editCourseIndex, values);
        console.log("Course data updated successfully");

        form.resetFields();
        setIsModalVisible(false);
        setEditCourseIndex(null);
      } catch (error) {
        console.error("Error updating course data:", error);
      }
    } else {
      const newCourse: Course = {
        courseName: values.courseName,
        instructorName: values.instructorName,
        duration: values.duration,
        courseDescription: values.courseDescription,
        seats: values.seats,
        price: values.price,
        language: values.language,
        lessons: values.lessons,
        enrolled: values.enrolled,
        courseContent: values.courseContent,
      };

      // Prepare payload with userId
      const payload = {
        ...newCourse,
        userId: userId
      };

      console.log('🚀 Submitting course with payload:', payload);

      axios.post<Course>(`${URLS.DEV}/v1/create-course`, payload)
        .then(response => {
          console.log('Course added successfully:', response.data);
          setSuccessData([...successData, response.data]);
          form.resetFields();
          setIsModalVisible(false);
          setEditCourseIndex(null);
        })
        .catch(error => {
          console.error('Error adding course:', error);
        });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get role and userId from localStorage
        const userDetailsString = localStorage.getItem('userDetails');
        const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
        const role = userDetails?.role;
        const userId = userDetails?.userId;

        let url = `https://service.ireedindia.com/v1/find-course?pageNumber=0&size=1000`;

        // If not admin, add userId as param
        if (role !== 'admin' && userId) {
          url += `&userId=${encodeURIComponent(userId)}`;
        }

        console.log('🔗 Fetching courses from:', url);

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        const responseData = await response.json();
        console.log('Fetched data:', responseData.data);
        setSuccessData(responseData.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }

    fetchData();
  }, []);

  const handleEdit = (record: Course) => {
    form.setFieldsValue({
      courseName: record.courseName,
      instructorName: record.instructorName,
      duration: record.duration,
      courseDescription: record.courseDescription,
      seats: record.seats,
      price: record.price,
      language: record.language,
      lessons: record.lessons,
      enrolled: record.enrolled,
      courseContent: record.courseContent,
    });
    setIsModalVisible(true);
    setEditCourseIndex(record._id);
  };

  const handleDelete = async (courseId: any) => {
    try {
      await axios.delete(`${URLS.DEV}/v1/delete-course/${courseId}`);
      console.log("Course deleted:", courseId);
      const updatedCourses = successData.filter(course => course._id !== courseId);
      setSuccessData(updatedCourses);
    } catch (error) {
      console.error("Error deleting course:", error);
    }
  };

  // Handle publish/unpublish course - Only for admin
  const handlePublish = async (courseId: any, currentStatus: boolean) => {
    try {
      const response = await axios.patch(`${URLS.DEV}/v1/edit-course/${courseId}`, {
        published: !currentStatus
      });
      console.log("Course publish status updated:", response.data);
      
      // Update local state
      const updatedCourses = successData.map(course =>
        course._id === courseId ? { ...course, published: !currentStatus } : course
      );
      setSuccessData(updatedCourses);
    } catch (error) {
      console.error("Error updating publish status:", error);
    }
  };

  const columns = [
    {
      title: "Course Name",
      dataIndex: "courseName",
      key: "courseName",
      width: 200,
    },
    {
      title: "Instructor",
      dataIndex: "instructorName",
      key: "instructorName",
      width: 150,
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      width: 100,
    },
    {
      title: "Seats",
      dataIndex: "seats",
      key: "seats",
      width: 80,
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      width: 100,
      render: (price: string) => `₹${price}`,
    },
    {
      title: "Language",
      dataIndex: "language",
      key: "language",
      width: 100,
    },
    {
      title: "Lessons",
      dataIndex: "lessons",
      key: "lessons",
      width: 80,
    },
    {
      title: "Enrolled",
      dataIndex: "enrolled",
      key: "enrolled",
      width: 80,
    },
    {
      title: "Course Content",
      dataIndex: "courseContent",
      key: "courseContent",
      width: 150,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "published",
      key: "published",
      width: 120,
      render: (published: boolean, record: Course) => {
        const isPub = published ?? record.isPublished ?? false;
        return (
          <span style={{
            padding: '4px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            backgroundColor: isPub ? '#d1fae5' : '#fef3c7',
            color: isPub ? '#065f46' : '#92400e'
          }}>
            {isPub ? '✓ Published' : 'Draft'}
          </span>
        );
      },
    },
    // Admin-only columns
    ...(userRole === 'admin' ? [
      {
        title: "Created By",
        dataIndex: "userPostDataInfo",
        key: "createdBy",
        width: 200,
        render: (userPostDataInfo: Course['userPostDataInfo']) => {
          if (!userPostDataInfo || userPostDataInfo.length === 0) return 'N/A';
          const user = userPostDataInfo[0];
          return (
            <div style={{ fontSize: '12px' }}>
              <div style={{ fontWeight: '600', color: '#1f2937' }}>{user.name}</div>
              <div style={{ color: '#6b7280' }}>{user.emailId}</div>
              <div style={{ color: '#1890ff', fontSize: '11px' }}>{user.role}</div>
            </div>
          );
        },
      },
      {
        title: "Created At",
        dataIndex: "userPostDataInfo",
        key: "createdAt",
        width: 180,
        render: (userPostDataInfo: Course['userPostDataInfo']) => {
          if (!userPostDataInfo || userPostDataInfo.length === 0) return 'N/A';
          const user = userPostDataInfo[0];
          const date = new Date(user.createdAt);
          return (
            <div style={{ fontSize: '12px', color: '#4b5563' }}>
              <div>{date.toLocaleDateString()}</div>
              <div style={{ color: '#9ca3af' }}>{date.toLocaleTimeString()}</div>
            </div>
          );
        },
      },
      {
        title: "Updated At",
        dataIndex: "userPostDataInfo",
        key: "updatedAt",
        width: 180,
        render: (userPostDataInfo: Course['userPostDataInfo']) => {
          if (!userPostDataInfo || userPostDataInfo.length === 0) return 'N/A';
          const user = userPostDataInfo[0];
          const date = new Date(user.updatedAt);
          return (
            <div style={{ fontSize: '12px', color: '#4b5563' }}>
              <div>{date.toLocaleDateString()}</div>
              <div style={{ color: '#9ca3af' }}>{date.toLocaleTimeString()}</div>
            </div>
          );
        },
      },
    ] : []),

    
    {
      title: "Actions",
      key: "actions",
      width: 210,
      fixed: 'right' as 'right',
      render: (_: any, record: Course) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          {/* Show Publish button only for admin users */}
          {userRole === 'admin' && (
            <Button 
              type={(record.published ?? record.isPublished) ? "default" : "primary"}
              onClick={() => handlePublish(record._id, (record.published ?? record.isPublished) || false)}
              style={{ minWidth: 90 }}
            >
              {(record.published ?? record.isPublished) ? 'Unpublish' : 'Publish'}
            </Button>
          )}
          {/* Show Delete button only for admin users */}
          {userRole === 'admin' && (
            <Button 
              danger
              icon={<DeleteOutlined />} 
              onClick={() => handleDelete(record._id)}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      <div style={{ 
        background: 'white', 
        padding: '24px', 
        borderRadius: '12px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '24px'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>
              📚 Courses Management
            </h2>
            <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>
              Manage your courses and track enrollments
            </p>
          </div>
          <Button 
            type="primary" 
            onClick={() => setIsModalVisible(true)}
            size="large"
            style={{ 
              background: '#667eea',
              borderColor: '#667eea',
              borderRadius: '8px',
              fontWeight: '500'
            }}
          >
            ➕ Add Course
          </Button>
        </div>
        {/* Publish/Unpublish Filter for Admin */}
        {userRole === 'admin' && (
          <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontWeight: 500, fontSize: 15 }}>Filter:</span>
            <select
              value={publishFilter}
              onChange={e => setPublishFilter(e.target.value as 'all' | 'published' | 'unpublished')}
              style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 15 }}
            >
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="unpublished">Unpublished</option>
            </select>
          </div>
        )}
        <Table 
          columns={columns} 
          dataSource={
            userRole === 'admin'
              ? (publishFilter === 'all'
                  ? successData
                  : successData.filter(course => {
                      const isPub = course.published ?? course.isPublished ?? false;
                      return publishFilter === 'published' ? isPub : !isPub;
                    })
                )
              : successData
          }
          rowKey="_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} courses`,
          }}
          style={{ marginTop: '16px' }}
          // scroll={{ x: 1200 }}
        />
      </div>
      
      <Modal
        title={
          <div style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#1f2937',
            padding: '8px 0'
          }}>
            {editCourseIndex !== null ? "✏️ Edit Course" : "➕ Add New Course"}
          </div>
        }
        visible={isModalVisible}
        onCancel={() => {
          form.resetFields();
          setIsModalVisible(false);
          setEditCourseIndex(null);
        }}
        footer={null}
        width={700}
        style={{ top: 20 }}
      >
        <Form
          form={form}
          name="courses"
          onFinish={onFinish}
          layout="vertical"
          style={{ marginTop: '20px' }}
        >
          <Form.Item
            label="Course Name"
            name="courseName"
            rules={[{ required: true, message: "Please input the course name!" }]}
          >
            <Input placeholder="e.g., React Advanced Course" size="large" />
          </Form.Item>
          
          <Form.Item label="Instructor Name" name="instructorName">
            <Input placeholder="e.g., John Doe" size="large" />
          </Form.Item>
          
          <Form.Item label="Duration" name="duration">
            <Input placeholder="e.g., 6 weeks" size="large" />
          </Form.Item>
          
          <Form.Item label="Course Description" name="courseDescription">
            <TextArea 
              rows={4} 
              placeholder="Enter detailed course description..."
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item label="Available Seats" name="seats">
              <Input placeholder="e.g., 50" size="large" type="text" />
            </Form.Item>
            
            <Form.Item label="Price (₹)" name="price">
              <Input placeholder="e.g., 9999" size="large" type="text" />
            </Form.Item>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item label="Language" name="language">
              <Input placeholder="e.g., English" size="large" />
            </Form.Item>
            
            <Form.Item label="Number of Lessons" name="lessons">
              <Input placeholder="e.g., 45" size="large" type="text" />
            </Form.Item>
          </div>
          
          <Form.Item label="Students Enrolled" name="enrolled">
            <Input placeholder="e.g., 120" size="large" type="text" />
          </Form.Item>
          
          <Form.Item label="Course Content/Syllabus" name="courseContent">
            <TextArea 
              rows={3} 
              placeholder="Enter course content, modules, topics covered..."
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>
          
          <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button 
                onClick={() => {
                  form.resetFields();
                  setIsModalVisible(false);
                  setEditCourseIndex(null);
                }}
                size="large"
                style={{ borderRadius: '8px' }}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                size="large"
                style={{ 
                  background: '#667eea',
                  borderColor: '#667eea',
                  borderRadius: '8px',
                  minWidth: '120px'
                }}
              >
                {editCourseIndex !== null ? "Update Course" : "Add Course"}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CoursesForm;
